import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { 
  ArrowLeft, Save, Receipt, Calendar, Tag, User, Building2, FileText, 
  Paperclip, X, Upload, Image, Plus, Banknote, Smartphone, CreditCard, 
  IndianRupee, Clock, CheckCircle, Search
} from 'lucide-react';
import { useBusiness } from '../../contexts/BusinessContext';
import { toast } from 'react-hot-toast';
import api from '../../services/api';
import { expensesAPI } from '../../services/part2-api';
import { getApiErrorMessage } from '../../utils/apiErrors';

const PAYMENT_MODES = [
  { value: 'CASH', label: 'Cash', icon: Banknote },
  { value: 'UPI', label: 'UPI', icon: Smartphone },
  { value: 'BANK', label: 'Bank Transfer', icon: Building2 },
  { value: 'CARD', label: 'Card', icon: CreditCard },
  { value: 'CHEQUE', label: 'Cheque', icon: FileText },
];

const DEFAULT_CATEGORIES = [
  'Rent', 'Electricity', 'Internet/Phone', 'Salaries & Advances', 'Transportation',
  'Fuel', 'Maintenance', 'Office Supplies', 'Packaging', 'Marketing',
  'Professional Fees', 'Insurance', 'Bank Fees', 'Software', 'Travel',
  'Meals', 'Raw Material', 'Commissions', 'Other'
];

const RecordExpensePage = () => {
  const { businessId, id: expenseId } = useParams();
  const navigate = useNavigate();
  const { activeBusiness } = useBusiness();
  const bid = businessId || activeBusiness?.id;
  const isEdit = !!expenseId;
  const fileInputRef = useRef(null);

  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [categories, setCategories] = useState(DEFAULT_CATEGORIES);
  const [vendors, setVendors] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [staff, setStaff] = useState([]);
  const [accounts, setAccounts] = useState([]);
  const [showNewCategory, setShowNewCategory] = useState(false);
  const [newCategoryName, setNewCategoryName] = useState('');
  const [vendorSearch, setVendorSearch] = useState('');
  const [showVendorDropdown, setShowVendorDropdown] = useState(false);

  const [formData, setFormData] = useState({
    date: new Date().toISOString().split('T')[0],
    category: '',
    amount: '',
    vendor_id: '',
    vendor_name: '',
    reference_no: '',
    notes: '',
    billable: false,
    customer_id: '',
    customer_name: '',
    payment_mode: 'CASH',
    paid_from_account_id: '',
    paid_from_account_name: '',
    paid_by_user_id: '',
    paid_by_name: '',
    attachments: [],
  });

  const [uploadedFiles, setUploadedFiles] = useState([]);

  useEffect(() => {
    fetchVendors();
    fetchCustomers();
    fetchStaff();
    fetchAccounts();
    if (isEdit) fetchExpense();
  }, [isEdit]);

  const fetchVendors = async () => {
    try {
      const { data } = await api.get('/parties', { params: { type: 'supplier' } });
      setVendors(data.parties || data || []);
    } catch (e) { toast.error(getApiErrorMessage(e, 'Failed to load vendors')); }
  };

  const fetchCustomers = async () => {
    try {
      const { data } = await api.get('/parties', { params: { type: 'customer' } });
      setCustomers(data.parties || data || []);
    } catch (e) { toast.error(getApiErrorMessage(e, 'Failed to load customers')); }
  };

  const fetchStaff = async () => {
    try {
      const { data } = await api.get('/settings/users/field-staff');
      setStaff(data.staff || data || []);
    } catch (e) { toast.error(getApiErrorMessage(e, 'Failed to load staff')); }
  };

  const fetchAccounts = async () => {
    try {
      const { data } = await api.get('/cash-bank/accounts');
      setAccounts(data.accounts || data || []);
    } catch (e) { toast.error(getApiErrorMessage(e, 'Failed to load accounts')); }
  };

  const fetchExpense = async () => {
    setLoading(true);
    try {
      const { data } = await expensesAPI.get(expenseId);
      const exp = data.expense || data;
      setFormData({ ...exp, amount: exp.amount || '' });
      setVendorSearch(exp.vendor_name || '');
    } catch (e) { toast.error(getApiErrorMessage(e, 'Failed to load expense')); }
    setLoading(false);
  };

  const selectVendor = (vendor) => {
    setFormData(prev => ({ ...prev, vendor_id: vendor.id, vendor_name: vendor.name }));
    setVendorSearch(vendor.name);
    setShowVendorDropdown(false);
  };

  const handleFileUpload = (e) => {
    const files = Array.from(e.target.files);
    files.forEach(file => {
      const reader = new FileReader();
      reader.onloadend = () => {
        setUploadedFiles(prev => [...prev, { name: file.name, data: reader.result, type: file.type }]);
      };
      reader.readAsDataURL(file);
    });
  };

  const removeFile = (index) => {
    setUploadedFiles(prev => prev.filter((_, i) => i !== index));
  };

  const handleSubmit = async () => {
    if (!formData.category) { toast.error('Please select a category'); return; }
    if (!formData.amount || parseFloat(formData.amount) <= 0) { toast.error('Please enter a valid amount'); return; }

    setSaving(true);
    try {
      const payload = { ...formData, amount: parseFloat(formData.amount), attachments: uploadedFiles.map(f => f.name) };

      if (isEdit) {
        await expensesAPI.update(expenseId, payload);
      } else {
        await expensesAPI.create(payload);
      }

      toast.success(isEdit ? 'Expense updated' : 'Expense recorded');
      navigate(`/business/${bid}/expenses`);
    } catch (e) {
      toast.error(getApiErrorMessage(e, 'Error saving expense'));
    }
    setSaving(false);
  };

  const addNewCategory = () => {
    if (newCategoryName.trim() && !categories.includes(newCategoryName.trim())) {
      setCategories(prev => [...prev, newCategoryName.trim()]);
      setFormData(prev => ({ ...prev, category: newCategoryName.trim() }));
    }
    setNewCategoryName('');
    setShowNewCategory(false);
  };

  const filteredVendors = vendors.filter(v => v.name?.toLowerCase().includes(vendorSearch.toLowerCase()));

  if (loading) {
    return (
      <div className="h-full flex items-center justify-center bg-[#F8F9FB]">
        <div className="text-center">
          <div className="w-10 h-10 border-[3px] border-slate-200 border-t-teal-600 rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-[14px] text-slate-500">Loading expense...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="h-full flex flex-col bg-[#F8F9FB] animate-pageFromRight">
      {/* PREMIUM HEADER */}
      <div className="flex-shrink-0 bg-white border-b border-slate-200 shadow-sm">
        <div className="max-w-[1600px] mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <button
                onClick={() => navigate(-1)}
                className="group flex items-center justify-center w-10 h-10 rounded-xl bg-slate-100 hover:bg-slate-200 transition-all duration-200 active:scale-95"
              >
                <ArrowLeft className="w-5 h-5 text-slate-600 group-hover:text-slate-800 transition-colors" />
              </button>
              <div>
                <h1 className="text-[20px] font-bold text-slate-800">{isEdit ? 'Edit Expense' : 'Record Expense'}</h1>
                <p className="text-[13px] text-slate-500 mt-0.5">Track business expense</p>
              </div>
            </div>

            <div className="flex items-center gap-2">
              <button
                onClick={() => navigate(-1)}
                className="inline-flex items-center gap-2 px-4 py-2.5 text-slate-700 bg-white border border-slate-300 rounded-xl hover:bg-slate-50 transition-all duration-200 text-[13px] font-medium"
              >
                Cancel
              </button>
              <button
                onClick={handleSubmit}
                disabled={saving}
                className="inline-flex items-center gap-2 px-5 py-2.5 bg-teal-600 text-white rounded-xl hover:bg-teal-700 transition-all duration-200 text-[13px] font-medium shadow-sm shadow-teal-600/20"
              >
                <Save className="w-4 h-4" />
                {saving ? 'Saving...' : isEdit ? 'Update' : 'Save Expense'}
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* MAIN CONTENT - Premium 3-Column Layout */}
      <div className="flex-1 overflow-y-auto px-6 py-5">
        <div className="max-w-[1600px] mx-auto">
          <div className="grid grid-cols-12 gap-5">
            
            {/* LEFT COLUMN - Expense Details (3 cols) */}
            <div className="col-span-12 lg:col-span-3 space-y-4">
              
              {/* Category Card */}
              <div className="bg-white rounded-2xl p-5 shadow-sm border border-slate-100 hover:shadow-md transition-shadow duration-200">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-teal-500 to-teal-600 flex items-center justify-center shadow-sm">
                    <Tag className="w-4 h-4 text-white" />
                  </div>
                  <h2 className="text-[14px] font-semibold text-slate-800">Category *</h2>
                </div>
                
                <select
                  value={formData.category}
                  onChange={(e) => setFormData(prev => ({ ...prev, category: e.target.value }))}
                  className="w-full px-3 py-2.5 bg-slate-50 border border-slate-200 rounded-xl text-[13px] focus:ring-2 focus:ring-teal-500/20 focus:border-teal-500"
                >
                  <option value="">Select category...</option>
                  {categories.map(cat => (
                    <option key={cat} value={cat}>{cat}</option>
                  ))}
                </select>
                
                {!showNewCategory ? (
                  <button onClick={() => setShowNewCategory(true)} className="mt-2 text-[12px] text-teal-600 hover:underline flex items-center gap-1">
                    <Plus className="w-3 h-3" /> Add new
                  </button>
                ) : (
                  <div className="mt-2 flex gap-2">
                    <input
                      type="text"
                      placeholder="New category"
                      value={newCategoryName}
                      onChange={(e) => setNewCategoryName(e.target.value)}
                      className="flex-1 px-3 py-2 bg-slate-50 border border-slate-200 rounded-lg text-[12px]"
                    />
                    <button onClick={addNewCategory} className="px-3 py-2 bg-teal-600 text-white rounded-lg text-[11px]">Add</button>
                  </div>
                )}
              </div>

              {/* Vendor Card */}
              <div className="bg-white rounded-2xl p-5 shadow-sm border border-slate-100 hover:shadow-md transition-shadow duration-200">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-slate-500 to-slate-600 flex items-center justify-center shadow-sm">
                    <Building2 className="w-4 h-4 text-white" />
                  </div>
                  <h2 className="text-[14px] font-semibold text-slate-800">Vendor</h2>
                </div>
                
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                  <input
                    type="text"
                    placeholder="Search vendor..."
                    value={vendorSearch}
                    onChange={(e) => { setVendorSearch(e.target.value); setShowVendorDropdown(true); }}
                    onFocus={() => setShowVendorDropdown(true)}
                    className="w-full pl-10 pr-4 py-2.5 bg-slate-50 border border-slate-200 rounded-xl text-[13px] focus:ring-2 focus:ring-teal-500/20 focus:border-teal-500"
                  />
                  {showVendorDropdown && filteredVendors.length > 0 && vendorSearch && (
                    <div className="absolute z-20 mt-1 w-full bg-white border border-slate-200 rounded-xl shadow-lg max-h-40 overflow-y-auto">
                      {filteredVendors.slice(0, 5).map(v => (
                        <div key={v.id} onClick={() => selectVendor(v)} className="px-4 py-3 hover:bg-teal-50 cursor-pointer text-[13px] border-b border-slate-100 last:border-0">
                          {v.name}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>

              {/* Paid By Card */}
              <div className="bg-white rounded-2xl p-5 shadow-sm border border-slate-100 hover:shadow-md transition-shadow duration-200">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-amber-400 to-orange-500 flex items-center justify-center shadow-sm">
                    <User className="w-4 h-4 text-white" />
                  </div>
                  <h2 className="text-[14px] font-semibold text-slate-800">Paid By</h2>
                </div>
                
                <select
                  value={formData.paid_by_user_id}
                  onChange={(e) => {
                    const user = staff.find(s => s.id === e.target.value);
                    setFormData(prev => ({ ...prev, paid_by_user_id: e.target.value, paid_by_name: user?.full_name || user?.name || '' }));
                  }}
                  className="w-full px-3 py-2.5 bg-slate-50 border border-slate-200 rounded-xl text-[13px] focus:ring-2 focus:ring-teal-500/20 focus:border-teal-500"
                >
                  <option value="">Select staff...</option>
                  {staff.map(s => <option key={s.id} value={s.id}>{s.full_name || s.name}</option>)}
                </select>
              </div>
            </div>

            {/* CENTER COLUMN - Amount & Payment (6 cols) */}
            <div className="col-span-12 lg:col-span-6 space-y-4">
              
              {/* Amount & Date Card */}
              <div className="bg-white rounded-2xl shadow-sm border border-slate-100 overflow-hidden hover:shadow-md transition-shadow duration-200">
                <div className="px-5 py-4 border-b border-slate-100">
                  <div className="flex items-center gap-3">
                    <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-blue-500 to-indigo-600 flex items-center justify-center shadow-sm">
                      <Receipt className="w-4 h-4 text-white" />
                    </div>
                    <h2 className="text-[14px] font-semibold text-slate-800">Expense Details</h2>
                  </div>
                </div>
                
                <div className="p-5">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="text-[12px] text-slate-500 block mb-2">Expense Date *</label>
                      <input
                        type="date"
                        value={formData.date}
                        onChange={(e) => setFormData(prev => ({ ...prev, date: e.target.value }))}
                        className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl text-[14px] focus:ring-2 focus:ring-teal-500/20 focus:border-teal-500"
                      />
                    </div>
                    <div>
                      <label className="text-[12px] text-slate-500 block mb-2">Amount *</label>
                      <div className="relative">
                        <span className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400 font-medium">₹</span>
                        <input
                          type="number"
                          min="0"
                          step="0.01"
                          value={formData.amount}
                          onChange={(e) => setFormData(prev => ({ ...prev, amount: e.target.value }))}
                          placeholder="0.00"
                          className="w-full pl-9 pr-4 py-3 bg-slate-50 border border-slate-200 rounded-xl text-[14px] font-medium focus:ring-2 focus:ring-teal-500/20 focus:border-teal-500"
                        />
                      </div>
                    </div>
                    <div>
                      <label className="text-[12px] text-slate-500 block mb-2">Reference #</label>
                      <input
                        type="text"
                        placeholder="Invoice/Receipt number"
                        value={formData.reference_no}
                        onChange={(e) => setFormData(prev => ({ ...prev, reference_no: e.target.value }))}
                        className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl text-[14px] focus:ring-2 focus:ring-teal-500/20 focus:border-teal-500"
                      />
                    </div>
                    <div>
                      <label className="text-[12px] text-slate-500 block mb-2">Paid From Account</label>
                      <select
                        value={formData.paid_from_account_id}
                        onChange={(e) => {
                          const acc = accounts.find(a => a.id === e.target.value);
                          setFormData(prev => ({ ...prev, paid_from_account_id: e.target.value, paid_from_account_name: acc?.name || '' }));
                        }}
                        className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl text-[14px] focus:ring-2 focus:ring-teal-500/20 focus:border-teal-500"
                      >
                        <option value="">Select account...</option>
                        {accounts.map(a => <option key={a.id} value={a.id}>{a.name}</option>)}
                      </select>
                    </div>
                  </div>
                </div>
              </div>

              {/* Payment Mode Card */}
              <div className="bg-white rounded-2xl shadow-sm border border-slate-100 overflow-hidden hover:shadow-md transition-shadow duration-200">
                <div className="px-5 py-4 border-b border-slate-100">
                  <div className="flex items-center gap-3">
                    <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-emerald-500 to-teal-600 flex items-center justify-center shadow-sm">
                      <Banknote className="w-4 h-4 text-white" />
                    </div>
                    <h2 className="text-[14px] font-semibold text-slate-800">Payment Mode</h2>
                  </div>
                </div>
                
                <div className="p-5">
                  <div className="grid grid-cols-5 gap-2">
                    {PAYMENT_MODES.map((mode) => {
                      const ModeIcon = mode.icon;
                      const isSelected = formData.payment_mode === mode.value;
                      return (
                        <button
                          key={mode.value}
                          onClick={() => setFormData(prev => ({ ...prev, payment_mode: mode.value }))}
                          className={`flex flex-col items-center gap-2 p-3 rounded-xl border-2 transition-all duration-200 ${isSelected ? 'border-teal-500 bg-teal-50' : 'border-slate-200 hover:border-slate-300 bg-white'}`}
                        >
                          <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${isSelected ? 'bg-teal-500' : 'bg-slate-100'}`}>
                            <ModeIcon className={`w-4 h-4 ${isSelected ? 'text-white' : 'text-slate-500'}`} />
                          </div>
                          <span className={`text-[11px] font-medium ${isSelected ? 'text-teal-700' : 'text-slate-600'}`}>{mode.label}</span>
                        </button>
                      );
                    })}
                  </div>
                </div>
              </div>

              {/* Notes & Attachments */}
              <div className="bg-white rounded-2xl shadow-sm border border-slate-100 overflow-hidden hover:shadow-md transition-shadow duration-200">
                <div className="px-5 py-4 border-b border-slate-100">
                  <div className="flex items-center gap-3">
                    <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-amber-400 to-orange-500 flex items-center justify-center shadow-sm">
                      <FileText className="w-4 h-4 text-white" />
                    </div>
                    <h2 className="text-[14px] font-semibold text-slate-800">Notes & Receipts</h2>
                  </div>
                </div>
                
                <div className="p-5 space-y-4">
                  <textarea
                    placeholder="Add notes about this expense..."
                    value={formData.notes}
                    onChange={(e) => setFormData(prev => ({ ...prev, notes: e.target.value }))}
                    rows={2}
                    className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl text-[13px] focus:ring-2 focus:ring-teal-500/20 focus:border-teal-500 resize-none"
                  />
                  
                  <input type="file" ref={fileInputRef} onChange={handleFileUpload} multiple className="hidden" accept="image/*,.pdf" />
                  <button
                    onClick={() => fileInputRef.current?.click()}
                    className="w-full py-3 border-2 border-dashed border-slate-200 rounded-xl text-center hover:border-teal-400 hover:bg-teal-50/50 transition-colors flex items-center justify-center gap-2"
                  >
                    <Upload className="w-4 h-4 text-slate-400" />
                    <span className="text-[13px] text-slate-500">Upload receipts</span>
                  </button>
                  
                  {uploadedFiles.length > 0 && (
                    <div className="flex flex-wrap gap-2">
                      {uploadedFiles.map((file, idx) => (
                        <div key={idx} className="inline-flex items-center gap-2 px-3 py-2 bg-slate-50 rounded-lg">
                          {file.type.includes('image') ? <Image className="w-4 h-4 text-teal-600" /> : <FileText className="w-4 h-4 text-teal-600" />}
                          <span className="text-[12px] text-slate-700">{file.name}</span>
                          <button onClick={() => removeFile(idx)} className="text-slate-400 hover:text-red-500">
                            <X className="w-3 h-3" />
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* RIGHT COLUMN - Summary (3 cols) */}
            <div className="col-span-12 lg:col-span-3 space-y-4">
              
              {/* Summary Card */}
              <div className="bg-gradient-to-br from-teal-50 to-emerald-50 rounded-2xl p-5 shadow-sm border border-teal-200/60">
                <div className="flex items-center gap-3 mb-5">
                  <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-teal-500 to-emerald-600 flex items-center justify-center shadow-sm">
                    <IndianRupee className="w-4 h-4 text-white" />
                  </div>
                  <h2 className="text-[14px] font-semibold text-slate-800">Summary</h2>
                </div>
                
                <div className="space-y-3">
                  <div className="flex justify-between text-[13px]">
                    <span className="text-slate-600">Category</span>
                    <span className="text-slate-800 font-medium">{formData.category || '—'}</span>
                  </div>
                  <div className="flex justify-between text-[13px]">
                    <span className="text-slate-600">Date</span>
                    <span className="text-slate-800 font-medium">
                      {formData.date ? new Date(formData.date).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }) : '—'}
                    </span>
                  </div>
                  <div className="flex justify-between text-[13px]">
                    <span className="text-slate-600">Mode</span>
                    <span className="text-slate-800 font-medium capitalize">{formData.payment_mode?.toLowerCase() || '—'}</span>
                  </div>
                  {formData.vendor_name && (
                    <div className="flex justify-between text-[13px]">
                      <span className="text-slate-600">Vendor</span>
                      <span className="text-slate-800 font-medium">{formData.vendor_name}</span>
                    </div>
                  )}
                  
                  <div className="border-t border-teal-200 pt-4 mt-4">
                    <div className="flex justify-between items-center">
                      <span className="text-[14px] font-semibold text-slate-700">Total</span>
                      <span className="text-[22px] font-bold text-teal-600">
                        ₹{parseFloat(formData.amount || 0).toLocaleString('en-IN')}
                      </span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Billable Card */}
              <div className="bg-white rounded-2xl p-5 shadow-sm border border-slate-100 hover:shadow-md transition-shadow duration-200">
                <label className="flex items-center gap-3 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={formData.billable}
                    onChange={(e) => setFormData(prev => ({ ...prev, billable: e.target.checked }))}
                    className="w-5 h-5 rounded border-slate-300 text-teal-600 focus:ring-teal-500"
                  />
                  <div>
                    <p className="text-[13px] font-medium text-slate-700">Billable to Customer</p>
                    <p className="text-[11px] text-slate-500">Mark if customer should be billed</p>
                  </div>
                </label>
                
                {formData.billable && (
                  <div className="mt-4">
                    <select
                      value={formData.customer_id}
                      onChange={(e) => {
                        const cust = customers.find(c => c.id === e.target.value);
                        setFormData(prev => ({ ...prev, customer_id: e.target.value, customer_name: cust?.name || '' }));
                      }}
                      className="w-full px-3 py-2.5 bg-slate-50 border border-slate-200 rounded-xl text-[13px] focus:ring-2 focus:ring-teal-500/20 focus:border-teal-500"
                    >
                      <option value="">Select customer...</option>
                      {customers.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                    </select>
                  </div>
                )}
              </div>

              {/* Quick Actions */}
              <div className="bg-white rounded-2xl p-5 shadow-sm border border-slate-100">
                <h2 className="text-[14px] font-semibold text-slate-800 mb-4">Quick Actions</h2>
                <div className="space-y-2">
                  <button
                    onClick={handleSubmit}
                    disabled={saving}
                    className="w-full flex items-center gap-3 px-4 py-3 text-left rounded-xl bg-teal-50 hover:bg-teal-100 transition-colors duration-200"
                  >
                    <CheckCircle className="w-4 h-4 text-teal-600" />
                    <span className="text-[13px] font-medium text-teal-700">{isEdit ? 'Update' : 'Save'} Expense</span>
                  </button>
                  <button
                    onClick={() => navigate(-1)}
                    className="w-full flex items-center gap-3 px-4 py-3 text-left rounded-xl bg-slate-50 hover:bg-slate-100 transition-colors duration-200"
                  >
                    <ArrowLeft className="w-4 h-4 text-slate-600" />
                    <span className="text-[13px] font-medium text-slate-700">Cancel</span>
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default RecordExpensePage;
